import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { ApiStatus, VIEW_CONFIGS, SortConfig } from '../../../../../../utils/constants/commonConstants';
import { getDealFormConfig } from '../../../../../../store/investor/apiHelpers';
import { DEAL_VIEW_TYPES } from '../../../../../../store/investor/constants';
import { DataTableColumn } from '../../../../../../common/_custom';
import DealsRow from './components/DealsRow/DealsRow';
import styles from './DealsTable.module.scss';
import { isLenderType } from '../../../../../../utils/utils';
import { setDealStatusConfig } from '../../../../../../store/investor/action';
import { USER_TYPE } from '../../../../../../utils/constants/user';
import { getDealProbabilityService } from '../../../../../../store/investor/service';
import { JAVA_API_STATUS } from '../../../../../../utils/axios-interceptor';
import DebtProductOverview from '../../../../../DebtProducts/components/DebtProductOverview/DebtProductOverview';
import { DebtProductRowData } from '../../../../../DebtProducts/debtProductTypes';
import { mapDealProbabilityResponseToDebtProductRow } from '../../../utils/mapDealProbabilityResponse';

type Props = {
    isLoading: boolean;
    emptyScreenComponent: React.ReactNode;
    loadingComponent: React.ReactNode;
    setScrollPos: (scrollPos: number) => void;
    selectedCategory: string;
    sortConfig?: SortConfig;
    onSort?: (field: string) => void;
    currentColumns?: string[];
    onColumnsChange?: (columns: DataTableColumn[]) => void;
    onColumnsConfigChange?: (config: any) => void;
};

export interface HeaderConfig {
    id?: string;
    label?: string;
    greyText?: boolean;
    wrapText?: boolean;
    hideLabel?: boolean;
    type?: string;
    hideRow?: boolean;
}

const DealsTable: React.FC<Props> = ({
    isLoading,
    emptyScreenComponent,
    loadingComponent,
    setScrollPos,
    selectedCategory,
    sortConfig,
    onSort,
    currentColumns = [],
    onColumnsChange,
    onColumnsConfigChange,
}) => {
    const dispatch = useAppDispatch();
    const [mandateHeaders, setMandateHeaders] = useState<DataTableColumn[] | null>(null);
    const [dealsHeaders, setDealsHeaders] = useState<DataTableColumn[] | null>(null);
    const [lenderHeaders, setLenderHeaders] = useState<DataTableColumn[] | null>(null);
    const [configStatus, setConfigStatus] = useState<ApiStatus>(ApiStatus.IDLE);
    const hasNotifiedParent = useRef(false);

    const dealsData = useAppSelector((state) => state.investor.deals);
    const user = useAppSelector((state) => state.user);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const totalDealsCount = dealsData?.dealCount?.totalDeals ?? 0;

    const userType = user.userData.accountType;

    const [dealProbabilityDrawer, setDealProbabilityDrawer] = useState<{
        open: boolean;
        loading: boolean;
        data: DebtProductRowData | null;
        error: boolean;
    }>({
        open: false,
        loading: false,
        data: null,
        error: false,
    });

    const dealProbabilityInFlightRef = useRef(false);

    const handleDealProbabilityClose = useCallback(() => {
        setDealProbabilityDrawer({
            open: false,
            loading: false,
            data: null,
            error: false,
        });
    }, []);

    const handleDealProbabilityOpen = useCallback(async (referenceId: string) => {
        if (dealProbabilityInFlightRef.current) return;
        dealProbabilityInFlightRef.current = true;

        setDealProbabilityDrawer({
            open: true,
            loading: true,
            data: null,
            error: false,
        });

        try {
            const res = await getDealProbabilityService(referenceId);

            if (!res?.data) {
                throw new Error('Invalid response');
            }

            const responseData = res.data.responseData;
            if (responseData?.responseCode !== JAVA_API_STATUS.SUCCESS) {
                toast.error(responseData?.responseMessage || 'Could not load deal probability');
                setDealProbabilityDrawer({ open: true, loading: false, data: null, error: true });
                return;
            }

            const responseBody = res.data.responseBody;
            if (responseBody == null) {
                throw new Error('Missing response body');
            }

            const mapped = mapDealProbabilityResponseToDebtProductRow(responseBody);
            setDealProbabilityDrawer({ open: true, loading: false, data: mapped, error: false });
        } catch {
            setDealProbabilityDrawer({ open: true, loading: false, data: null, error: true });
            toast.error('Could not load deal probability');
        } finally {
            dealProbabilityInFlightRef.current = false;
        }
    }, []);

    const overlayScreen = useMemo(() => {
        if (isLoading && loadingComponent) return <div className={styles.Loading}>{loadingComponent}</div>;
        else if (totalDealsCount <= 0) if (emptyScreenComponent) return <div className={styles.Loading}>{emptyScreenComponent}</div>;
    }, [isLoading, loadingComponent, emptyScreenComponent, totalDealsCount]);

    const rawHeaders = useMemo(() => {
        if (isLenderType(userType)) return lenderHeaders;
        if (selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW) return dealsHeaders;
        return mandateHeaders;
    }, [lenderHeaders, dealsHeaders, mandateHeaders, selectedCategory, userType]);

    /** Rows and ordering come from the list API (includes dealProbability filter + sortConfig from backend). */
    const dealRowsForTable = dealsData.data;

    const fetchConfig = async () => {
        try {
            await getDealFormConfig(
                {
                    accountType: user.userData.accountType,
                    investorOrgId: platformData?.investorOrgId,
                    primaryEmail: user.userData?.primaryEmail,
                    configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
                },
                {
                    onSuccess: (configData, orgName) => {
                        if (user.userData.accountType === USER_TYPE.COMPANY) {
                            setDealsHeaders(configData?.borrowerDealViewHeaders ?? []);
                            setMandateHeaders(configData?.borrowerMandateViewHeaders ?? []);
                        } else {
                            setDealsHeaders(configData?.dealViewHeaders ?? []);
                            setMandateHeaders(configData?.mandateHeaders ?? []);
                        }
                        setLenderHeaders(configData?.lenderHeaders ?? []);
                        setConfigStatus(ApiStatus.SUCCESS);

                        dispatch(setDealStatusConfig(configData?.dealStatusDropdownOptions || {}));

                        // Notify parent component about column configuration only if it's the first time
                        // to prevent overriding user selections when config is refetched
                        if (onColumnsConfigChange && !hasNotifiedParent.current) {
                            onColumnsConfigChange(configData);
                            hasNotifiedParent.current = true;
                        }
                    },
                },
            );
        } catch (error) {
            console.error('Error fetching deal form config:', error);
            setConfigStatus(ApiStatus.ERROR);
        }
    };

    useEffect(() => {
        if (configStatus === ApiStatus.IDLE && platformData?.investorOrgId && user.userData?.primaryEmail) {
            setConfigStatus(ApiStatus.PENDING);
            fetchConfig();
        }
    }, [platformData, user, configStatus]);

    return (
        <>
            <DealsRow
                headers={rawHeaders ?? []}
                rows={dealRowsForTable}
                selectedCategory={selectedCategory}
                loading={isLoading}
                totalCount={totalDealsCount}
                emptyScreenComponent={emptyScreenComponent}
                sortConfig={sortConfig}
                onSort={onSort}
                dealStatusConfig={dealsData.dealStatusConfig}
                currentColumns={currentColumns}
                onColumnsChange={onColumnsChange}
                onDealProbabilityOpen={handleDealProbabilityOpen}
            />

            {dealProbabilityDrawer.open && (
                <DebtProductOverview
                    mode="dealProbability"
                    loading={dealProbabilityDrawer.loading}
                    loadError={dealProbabilityDrawer.error}
                    data={(dealProbabilityDrawer.data ?? {}) as DebtProductRowData}
                    closeModal={handleDealProbabilityClose}
                />
            )}
        </>
    );
};

export default DealsTable;
